import UnreadChatBadge from '../UnreadChatBadge';
import AdditionalMeasurementPanel from './AdditionalMeasurementPanel';
import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, Divider, IconButton, Sheet, Stack, Tooltip, Typography,
} from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServices';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import StarPicker from "./StarPicker";
import ReviewHistory, { ReviewItem } from "./ReviewHistory";
import { fmtWaiting, qaMeta, waitingColor } from "./qualityTheme";

/**
 * Карточка события в разделе контроля качества.
 *
 * props:
 *   card      — строка из /quality/events/
 *   onApprove — (stars) => Promise
 *   onRework  — () => void   (открывает окно доработки)
 *   onChat    — (chatId) => void
 *   onOpenEvent — (eventId) => void
 *   reviews   — история (подгружается по раскрытию)
 *   onExpand  — (eventId) => void
 */
export default function QualityEventCard({
  card, onApprove, onRework, onChat, onOpenEvent, onRefresh, reviews, onExpand, index = 0,
}) {
  const [stars, setStars] = useState(0);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { setStars(0); }, [card?.id, card?.qa_round]);

  const meta = qaMeta(card?.qa_state);
  const pending = card?.qa_state === 'pending';
  const canReview = pending && card?.can_review !== false;
  const waiting = fmtWaiting(card?.waiting_hours);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onExpand?.(card.id);
  };

  const approve = async () => {
    if (busy || !canReview) return;
    setBusy(true);
    try { await onApprove?.(stars); }
    finally { setBusy(false); }
  };

  return (
    <Sheet
      component={motion.div}
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.25) }}
      variant="outlined"
      sx={{
        borderRadius: '16px', overflow: 'hidden',
        borderColor: meta.border,
        // Левая цветная кромка — состояние читается боковым зрением,
        // без чтения подписей.
        borderLeft: '4px solid', borderLeftColor: meta.accent,
        bgcolor: 'background.surface',
        transition: 'box-shadow .18s, transform .18s',
        '&:hover': { boxShadow: 'md' },
      }}
    >
      <AdditionalMeasurementPanel eventId={card.id} cycle={card.additional_measurement} />
      {/* ── Шапка ── */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 1.25, background: `linear-gradient(135deg, ${meta.soft}, transparent 65%)` }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography level="title-sm" sx={{ fontWeight: 800 }}>
                №{card.deal_id ?? card.id}
              </Typography>
              <Chip size="sm" variant="soft"
                sx={{ bgcolor: meta.soft, color: meta.text, fontWeight: 700,
                      '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                {meta.label}
              </Chip>
              {card.qa_round > 1 && (
                <Tooltip size="sm" variant="soft" title="Сколько раз чертёж уходил на проверку">
                  <Chip size="sm" variant="outlined"
                    sx={{ '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                    круг {card.qa_round}
                  </Chip>
                </Tooltip>
              )}
              {card.is_overdue && (
                <Chip size="sm" variant="soft" color="danger"
                  sx={{ '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                  дедлайн прошёл
                </Chip>
              )}
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
              <PlaceRoundedIcon sx={{ fontSize: 14, color: 'text.tertiary', flexShrink: 0 }} />
              <Typography level="body-sm" noWrap sx={{ color: 'text.secondary' }}>
                {[card.city, card.address].filter(Boolean).join(', ') || 'без адреса'}
              </Typography>
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {card.chat_id && (
              <Tooltip size="sm" variant="soft" title="Чат разбора с чертёжником">
                <IconButton size="sm" variant="soft" color="primary"
                  onClick={() => onChat?.(card.chat_id)}>
                  <UnreadChatBadge chatId={card.chat_id}><ChatBubbleRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip size="sm" variant="soft" title="Открыть событие">
              <IconButton size="sm" variant="soft" color="neutral"
                onClick={() => onOpenEvent?.(card.id)}>
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ── Факты одной строкой ── */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Fact icon={<DesignServicesRoundedIcon sx={{ fontSize: 13 }} />}
                value={card.draftsman?.name || 'чертёжник не назначен'} />
          {card.square ? (
            <Fact icon={<SquareFootRoundedIcon sx={{ fontSize: 13 }} />}
                  value={`${card.square} м²`} />
          ) : null}
          {waiting && (
            <Chip size="sm" variant="soft" color={waitingColor(card.waiting_hours)}
              startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 12 }} />}
              sx={{ '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
              ждёт {waiting}
            </Chip>
          )}
        </Stack>
      </Box>

      {card.client_rejected && <Box sx={{ px: 2, pb: 1 }}>
        <Typography color="danger" level="title-sm">Клиент не принял работу</Typography>
        <Typography level="body-sm">{card.client_rejection_reason}</Typography>
      </Box>}
      {/* ── Последний вердикт ── */}
      {card.last_review && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1.25 }}>
          {pending && Number(card.last_review.round) < Number(card.qa_round) && (
            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
              Предыдущая проверка — текущий круг ещё не принят
            </Typography>
          )}
          <ReviewItem review={card.last_review} compact />
        </Box>
      )}

      {pending && !canReview && <Alert color="warning" sx={{ mx: 1.5, mb: 1.5 }}
        endDecorator={onRefresh ? <Button size="sm" variant="plain" onClick={onRefresh}>Обновить</Button> : null}>
        {card.review_blocker || 'Состояние события изменилось. Обновите карточку.'}
      </Alert>}
      {/* ── Действия ── */}
      {pending && (
        <>
          <Divider />
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <StarPicker value={stars} onChange={setStars} size={24} />
              <Stack direction="row" spacing={1}>
                <Button size="sm" variant="soft" color="danger"
                  startDecorator={<ReplayRoundedIcon />}
                  disabled={busy || !canReview}
                  onClick={() => onRework?.(card)}>
                  На доработку
                </Button>
                <Button size="sm" color="success" loading={busy} disabled={!canReview}
                  startDecorator={!busy ? <CheckCircleRoundedIcon /> : null}
                  onClick={approve}>
                  Принять
                </Button>
              </Stack>
            </Stack>
          </Box>
        </>
      )}

      {/* ── История ── */}
      {card.reviews_count > 0 && (
        <>
          <Divider />
          <Box
            onClick={toggle}
            sx={{
              px: { xs: 1.5, sm: 2 }, py: 0.75, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 0.75,
              '&:hover': { bgcolor: 'background.level1' },
            }}
          >
            <HistoryRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
            <Typography level="body-xs" sx={{ color: 'text.tertiary', flex: 1 }}>
              История проверок · {card.reviews_count}
            </Typography>
            <Box component={motion.span}
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.18 }}
              sx={{ lineHeight: 0, color: 'text.tertiary', fontSize: 12 }}>
              ▾
            </Box>
          </Box>
          <AnimatePresence initial={false}>
            {open && (
              <Box component={motion.div}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                sx={{ overflow: 'hidden', bgcolor: 'background.level1' }}
              >
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  {reviews === undefined
                    ? <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Загружаем…</Typography>
                    : <ReviewHistory reviews={reviews} compact />}
                </Box>
              </Box>
            )}
          </AnimatePresence>
        </>
      )}
    </Sheet>
  );
}

function Fact({ icon, value }) {
  return (
    <Stack direction="row" spacing={0.4} sx={{ alignItems: 'center', color: 'text.tertiary', minWidth: 0 }}>
      {icon}
      <Typography level="body-xs" noWrap sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}
